"use client";

import classNames from "classnames";
import PropTypes from "prop-types";
import elementType from "prop-types-extra/lib/elementType";
import { useContext, useEffect, useState } from "react";
import warning from "warning";
import Fade from "./Fade";
import TabContainerContext from "./TabContainerContext";
import TabContentContext from "./TabContentContext";
import {
  bsClass, getClassSet, prefix, splitBsPropsAndOmit,
} from "./utils/bootstrapUtils";
import createChainedFunction from "./utils/createChainedFunction";

const propTypes = {
  /**
   * Uniquely identify the `<TabPane>` among its siblings.
   */
  eventKey: PropTypes.any,

  /**
   * Use animation when showing or hiding `<TabPane>`s. Use `false` to disable,
   * `true` to enable the default `<Fade>` animation or any `<Transition>`
   * component.
   */
  animation: PropTypes.oneOfType([PropTypes.bool, elementType]),

  /** @private * */
  id: PropTypes.string,

  /** @private * */
  "aria-labelledby": PropTypes.string,

  /**
   * If not explicitly specified and rendered in the context of a
   * `<TabContent>`, the `bsClass` of the `<TabContent>` suffixed by `-pane`.
   * If otherwise not explicitly specified, `tab-pane`.
   */
  bsClass: PropTypes.string,

  /**
   * Transition onEnter callback when animation is not `false`
   */
  onEnter: PropTypes.func,

  /**
   * Transition onEntering callback when animation is not `false`
   */
  onEntering: PropTypes.func,

  /**
   * Transition onEntered callback when animation is not `false`
   */
  onEntered: PropTypes.func,

  /**
   * Transition onExit callback when animation is not `false`
   */
  onExit: PropTypes.func,

  /**
   * Transition onExiting callback when animation is not `false`
   */
  onExiting: PropTypes.func,

  /**
   * Transition onExited callback when animation is not `false`
   */
  onExited: PropTypes.func,

  /**
   * Wait until the first "enter" transition to mount the tab (add it to the DOM)
   */
  mountOnEnter: PropTypes.bool,

  /**
   * Unmount the tab (remove it from the DOM) when it is no longer visible
   */
  unmountOnExit: PropTypes.bool,
};

function TabPane(props) {
  const { animation, eventKey } = props;
  const [inPane, setInPane] = useState(false);
  const { $bs_tabContainer: tabContainer } = useContext(TabContainerContext);
  const { $bs_tabContent: tabContent } = useContext(TabContentContext);

  // getChildContext() {
  //   return {
  //     $bs_tabContainer: null,
  //   };
  // }

  const getAnimation = () => {
    if (animation != null) {
      return animation;
    }

    return tabContent && tabContent.animation;
  };

  const handleEnter = () => {
    if (!tabContent) {
      return;
    }

    setInPane(tabContent.onPaneEnter(this, eventKey));
  };

  const handleExited = () => {
    if (!tabContent) {
      return;
    }

    tabContent.onPaneExited(this);
    setInPane(false);
  };

  const isActive = () => {
    const activeKey = tabContent && tabContent.activeKey;

    return eventKey === activeKey;
  };

  const shouldBeIn = () => getAnimation() && isActive();

  useEffect(() => {
    if (shouldBeIn()) {
      handleEnter();
    }
  }, []);

  useEffect(() => {
    if (inPane) {
      if (!shouldBeIn()) {
        // We shouldn't be active any more. Notify the parent.
        handleExited();
      }
    } else if (shouldBeIn()) {
      // We are the active child. Notify the parent.
      handleEnter();
    }

    return () => {
      if (inPane) {
        handleExited();
      }
    };
  }, []);

  const {
    className,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    mountOnEnter: propsMountOnEnter,
    unmountOnExit: propsUnmountOnExit,
  } = props;

  const [bsProps, elementProps] = splitBsPropsAndOmit(props, ["animation"]);

  const active = isActive();
  const updatedAnimation = getAnimation();

  const mountOnEnter = propsMountOnEnter != null
    ? propsMountOnEnter : tabContent && tabContent.mountOnEnter;
  const unmountOnExit = propsUnmountOnExit != null
    ? propsUnmountOnExit : tabContent && tabContent.unmountOnExit;

  if (!active && !updatedAnimation && unmountOnExit) {
    return null;
  }

  const Transition = updatedAnimation === true ? Fade : updatedAnimation || null;

  if (tabContent) {
    bsProps.bsClass = prefix(tabContent, "pane");
  }

  const classes = {
    ...getClassSet(bsProps),
    active,
  };

  if (tabContainer) {
    warning(!elementProps.id && !elementProps["aria-labelledby"],
      "In the context of a `<TabContainer>`, `<TabPanes>` are given "
        + "generated `id` and `aria-labelledby` attributes for the sake of "
        + "proper component accessibility. Any provided ones will be ignored. "
        + "To control these attributes directly provide a `generateChildId` "
        + "prop to the parent `<TabContainer>`.");

    elementProps.id = tabContainer.getPaneId(eventKey);
    elementProps["aria-labelledby"] = tabContainer.getTabId(eventKey);
  }

  const pane = (
    <div
      {...elementProps}
      role="tabpanel"
      aria-hidden={!active}
      className={classNames(className, classes)}
    />
  );

  // Wrap with TabContainerContext to override context for nested Navs
  const wrappedPane = (
    <TabContainerContext.Provider value={{ $bs_tabContainer: undefined }}>
      {pane}
    </TabContainerContext.Provider>
  );

  if (Transition) {
    const exiting = tabContent && tabContent.exiting;

    return (
      <Transition
        in={active && !exiting}
        onEnter={createChainedFunction(handleEnter, onEnter)}
        onEntering={onEntering}
        onEntered={onEntered}
        onExit={onExit}
        onExiting={onExiting}
        onExited={createChainedFunction(handleExited, onExited)}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
      >
        {wrappedPane}
      </Transition>
    );
  }

  return wrappedPane;
}

TabPane.propTypes = propTypes;

export default bsClass("tab-pane", TabPane);
